import React from 'react';
import PropTypes from 'prop-types';
import { ChevronDown } from 'lucide-react';
import { WorkflowCard } from './WorkflowCard';
import styles from './WorkflowSection.module.css';

export const WorkflowSection = ({ 
  title, 
  subtitle,
  count, 
  tickets, 
  type, 
  onAction,
  expandedAlerts,
  onAlertToggle,
  renderAlertDetails
}) => {
  const getSectionClass = () => {
    switch(type) {
      case 'intervention': return styles.interventionSection;
      case 'proposed': return styles.proposedSection;
      case 'completed': return styles.completedSection;
      default: return '';
    }
  };

  return (
    <div className={`${styles.section} ${getSectionClass()}`}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <h3 className={styles.title}>
            <div className={styles.badge}></div>
            {title}
          </h3>
          <div className={styles.count}>{count.toString().padStart(2, '0')}</div>
        </div>
        {subtitle && (
          <p className={styles.subtitle}>{subtitle}</p>
        )}
      </div>
      
      <div className={styles.ticketList}>
        {/* Render new alert-based tickets */}
        {tickets.map((ticket, index) => {
          // Check if this is the new alert format or old ticket format
          if (ticket.alert_ids && ticket.details) {
            // New alert format - render as expandable alert
            const alertId = ticket.id;
            const isExpanded = expandedAlerts?.has(alertId);
            
            return (
              <div key={alertId} className={styles.alertItem}>
                <div className={styles.alertHeader}>
                  {/* Ticket button in top-right corner - always show */}
                  <div className={styles.ticketButtonContainer}>
                    {ticket.ticketInfo?.ticket_no && ticket.ticketInfo?.ticket_url ? (
                      <div
                        className={styles.ticketRibbonActive}
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(ticket.ticketInfo.ticket_url, "_blank", "noopener,noreferrer");
                        }}
                        title="Click to open ticket"
                      >
                        {ticket.ticketInfo.ticket_no}
                      </div>
                    ) : (
                      <div
                        className={styles.ticketRibbonInactive}
                        title="No ticket available for this alert"
                      >
                        No Ticket
                      </div>
                    )}
                  </div>
                  
                  <div 
                    className={styles.alertContent}
                    onClick={() => onAlertToggle?.(alertId)}
                  >
                    <div className={styles.alertInfo}> 
                      <div className={styles.alertMeta}>
                        <span className={styles.priority}>{ticket.priority}</span>
                        <span className={styles.status}>{ticket.status}</span>
                        {/* Show green Remediated pill only in completed column and if remediate_action.status is SUCCEEDED */}
                        {type === 'completed' && ticket.remediate_action?.status === 'SUCCEEDED' && (
                          <span className={styles.remediatedPill}>Remediated</span>
                        )}
                      </div>
                      <div className={styles.alertTitle}>{ticket.name}</div>
                      <span className={styles.counts}>
                          Triggered <span className={styles.alertInfostyle}>{ticket.count?.Triggered || 0} </span>  Resolved  <span className={styles.alertInfostyle}>{ticket.count?.Resolved || 0}  </span> 
                        </span>
                      
                    </div>
                  </div>
                </div>
                
                {/* Expandable chevron button at bottom center */}
                <div className={styles.expandToggle}>
                  <button 
                    className={styles.chevronButton}
                    onClick={() => onAlertToggle?.(alertId)}
                    aria-label={isExpanded ? 'Hide alert details' : 'Show alert details'}
                  >
                    <ChevronDown 
                      className={`${styles.chevronIcon} ${isExpanded ? styles.rotated : ''}`}
                    />
                  </button>
                </div>
                
                {isExpanded && (
                  <div className={styles.alertDetails}>
                    {renderAlertDetails?.(ticket)}
                  </div>
                )}
              </div>
            );
          } else {
            // Old ticket format - render as WorkflowCard
            return (
              <WorkflowCard
                key={ticket.id || `${type}-${index}`}
                ticket={ticket}
                type={type}
                onAction={onAction}
              />
            );
          }
        })}
        
        {tickets.length === 0 && (
          <div className={styles.emptyState}>
            <p>No tickets available</p>
          </div>
        )}
      </div>
    </div>
  );
};

WorkflowSection.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  count: PropTypes.number.isRequired,
  tickets: PropTypes.arrayOf(PropTypes.object).isRequired,
  type: PropTypes.oneOf(['intervention', 'proposed', 'completed']).isRequired,
  onAction: PropTypes.func,
  expandedAlerts: PropTypes.object,
  onAlertToggle: PropTypes.func,
  renderAlertDetails: PropTypes.func
};